import React from 'react';
import tw from 'twin.macro';
import {
  Matiere as IMatiere,
  Periode,
} from '@teetsh/app/src/types/programmations';
import { LabelledDateRange } from '@teetsh/app/src/types/zone';
import { PeriodeSemaineView } from './PeriodeSemaineView';
import { PeriodeDomaineView } from './PeriodeDomaineView';
import { DomainePeriodeView } from './DomainePeriodeView';
import { PrintView, RowTitleOrientation } from './types';
import { chunk } from 'lodash-es';
import { groupWeeksByPeriode } from '@teetsh/app/src/containers/Programmations/ProgV2/utils/groupWeeksByPeriodes';
import { shortenPeriodeLabel } from '@teetsh/app/src/containers/Programmations/ProgV2/Main/Periodes/shortenPeriodeLabel';
import { useTranslation } from 'react-i18next';
import { DEFAULT_NB_OF_WEEKS_PER_LINE } from '@teetsh/app/src/containers/Programmations/ProgV2/constants';
import { BreakBeforePageDivider } from '../Preview/BreakBeforePageDivider';

interface Props {
  matiere: IMatiere;
  periodes: Periode[];
  view: Extract<
    PrintView,
    'domaine_x_periode' | 'periode_x_domaine' | 'periode_x_semaine'
  >;
  schoolyearPeriodes: LabelledDateRange[];
  hideMatiereTitle?: boolean;
  groupWeeksByPeriodes?: boolean;
  rowTitleOrientation: RowTitleOrientation;
  preventCutting: boolean;
  breakBefore: boolean;
  isFirstMatiere?: boolean;
}

export function MatiereSingle(props: Props) {
  const { t } = useTranslation(['common', 'programmation']);
  const { matiere, periodes, view, schoolyearPeriodes } = props;
  const { weeksByPeriodes: weeksByPeriodesFound = [], otherWeeks = [] } =
    groupWeeksByPeriode(periodes, schoolyearPeriodes);
  const shouldBreakBeforeMatiere = props.breakBefore && !props.isFirstMatiere;

  return (
    <div
      tw="flex flex-col gap-y-8"
      css={[shouldBreakBeforeMatiere && tw`break-before-page`]}
    >
      {shouldBreakBeforeMatiere && <BreakBeforePageDivider />}
      {(() => {
        switch (view) {
          case 'domaine_x_periode': {
            if (!props.groupWeeksByPeriodes) {
              const groups = chunk(
                matiere.domaines,
                DEFAULT_NB_OF_WEEKS_PER_LINE,
              );
              return groups.map((group, i) => (
                <DomainePeriodeView
                  key={i}
                  isFirst={i === 0}
                  matiereTitle={
                    props.hideMatiereTitle ? undefined : matiere.name
                  }
                  domaines={group}
                  periodes={periodes}
                  rowTitleOrientation={props.rowTitleOrientation}
                  enforceNbColumns={
                    groups.length > 1 ? DEFAULT_NB_OF_WEEKS_PER_LINE : undefined
                  }
                  preventCutting={props.preventCutting}
                  breakBefore={props.breakBefore}
                />
              ));
            }

            const groups = chunk(
              matiere.domaines,
              DEFAULT_NB_OF_WEEKS_PER_LINE,
            );
            return groups.map((group, i) => (
              <React.Fragment key={i}>
                {weeksByPeriodesFound.map((p, ii) => (
                  <DomainePeriodeView
                    isFirst={ii === 0}
                    key={`${i}-${ii}`}
                    matiereTitle={
                      props.hideMatiereTitle ? undefined : matiere.name
                    }
                    domaines={group}
                    shortSectionTitle={shortenPeriodeLabel({
                      label: p.periode?.label,
                      t,
                    })}
                    sectionTitle={p.periode?.label}
                    periodes={p.weeks}
                    rowTitleOrientation={props.rowTitleOrientation}
                    enforceNbColumns={
                      groups.length > 1
                        ? DEFAULT_NB_OF_WEEKS_PER_LINE
                        : undefined
                    }
                    preventCutting={props.preventCutting}
                    breakBefore={props.breakBefore}
                  />
                ))}
              </React.Fragment>
            ));
          }
          case 'periode_x_domaine': {
            // Si on ne group pas par période, on limit simplement le nombre de semaines par ligne
            if (!props.groupWeeksByPeriodes) {
              const groups = chunk(periodes, DEFAULT_NB_OF_WEEKS_PER_LINE);
              return groups.map((group, i) => (
                <PeriodeDomaineView
                  key={i}
                  isFirst={i === 0}
                  matiereTitle={
                    props.hideMatiereTitle ? undefined : matiere.name
                  }
                  periodes={group}
                  matiere={matiere}
                  rowTitleOrientation={props.rowTitleOrientation}
                  enforceNbColumns={
                    groups.length > 1 ? DEFAULT_NB_OF_WEEKS_PER_LINE : undefined
                  }
                  preventCutting={props.preventCutting}
                  breakBefore={props.breakBefore}
                />
              ));
            }

            const groups = weeksByPeriodesFound.flatMap(g => {
              const grps = chunk(g.weeks, DEFAULT_NB_OF_WEEKS_PER_LINE);
              return grps.map(group => ({
                weeks: group,
                periode: g.periode,
              }));
            });
            const otherGroups = chunk(
              otherWeeks,
              DEFAULT_NB_OF_WEEKS_PER_LINE,
            ).map(c => ({
              weeks: c,
              periode: {
                id: 'other',
                label: '?',
              },
            }));

            const allGroups = [...groups, ...otherGroups];
            return allGroups.map((group, i) => (
              <PeriodeDomaineView
                key={i}
                isFirst={i === 0}
                matiereTitle={props.hideMatiereTitle ? undefined : matiere.name}
                periodeLabel={group.periode?.label}
                periodes={group.weeks}
                matiere={matiere}
                rowTitleOrientation={props.rowTitleOrientation}
                enforceNbColumns={
                  groups.length > 1 ? DEFAULT_NB_OF_WEEKS_PER_LINE : undefined
                }
                preventCutting={props.preventCutting}
                breakBefore={props.breakBefore}
              />
            ));
          }
          case 'periode_x_semaine':
            return (
              <PeriodeSemaineView
                preventCutting={props.preventCutting}
                breakBefore={props.breakBefore}
                matiere={matiere}
                periodes={periodes}
                schoolyearPeriodes={schoolyearPeriodes}
              />
            );
        }
      })()}
    </div>
  );
}
